import { useEffect, useMemo, useState } from 'react';
import { Shop } from '@/network/RestApi';
import { useUIStore } from '@/state/uiStore';
import { useCharacterStore } from '@/state/characterStore';
import { inventory } from '@/systems/InventorySystem';
import { ui } from './theme';
import { CloseButton } from './CloseButton';
import { ItemIcon } from './ItemIcon';

interface Product {
  id: number;
  code: string;
  name: string;
  itemTemplateId?: number | null;
  quantity: number;
  price: number;
  currency: 'diamond' | 'zen';
  vipRequired: number;
}

type Currency = Product['currency'];

const CURRENCY_META: Record<Currency, { label: string; symbol: string; color: string }> = {
  diamond: { label: 'Kim Cương', symbol: '◆', color: '#7ad7ff' },
  zen:     { label: 'Vàng Zen',  symbol: 'Z', color: '#ffd166' },
};

const TABS: Currency[] = ['diamond', 'zen'];

export function ShopPanel() {
  const close = () => useUIStore.getState().set('shop', false);
  const [list, setList] = useState<Product[]>([]);
  const [tab, setTab] = useState<Currency>('diamond');
  const showToast = useUIStore((s) => s.showToast);
  const ch = useCharacterStore((s) => s.current);

  useEffect(() => {
    Shop.products()
      .then((r) => setList((r as { products?: Product[] }).products ?? []))
      .catch(() => setList([]));
  }, []);

  const grouped = useMemo(() => {
    const g: Record<Currency, Product[]> = { diamond: [], zen: [] };
    for (const p of list) (g[p.currency] ?? (g[p.currency] = [])).push(p);
    return g;
  }, [list]);

  const buy = async (p: Product) => {
    if (!ch) {
      showToast('Chưa chọn nhân vật', 'error');
      return;
    }
    try {
      const res = (await Shop.buy({
        characterId: ch.id,
        productId: p.id,
        quantity: 1,
        idemKey: `${Date.now()}-${p.id}`,
        currency: p.currency,
      })) as { spent?: number; currency?: Currency; duplicate?: boolean };
      showToast(`Mua ${p.name} thành công`);
      inventory.setChar(ch.id);
      inventory.refresh().catch(() => {});
      if (!res?.duplicate) {
        const spent = res?.spent ?? p.price;
        const st = useCharacterStore.getState();
        if (p.currency === 'zen') {
          st.setCurrency({ zen: Math.max(0, st.zen - spent) });
        } else {
          st.setCurrency({ diamond: Math.max(0, st.diamond - spent) });
        }
      }
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const renderCard = (p: Product) => {
    const meta = CURRENCY_META[p.currency];
    return (
      <div
        key={p.id}
        style={{
          background: '#0b1220',
          border: '1px solid #324a5e',
          padding: 8,
          borderRadius: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          {p.itemTemplateId && (
            <div style={{ marginBottom: 4 }}>
              <ItemIcon templateId={p.itemTemplateId} size={40} />
            </div>
          )}
          <div style={{ fontWeight: 600 }}>{p.name}</div>
          {p.quantity > 1 && (
            <div style={{ fontSize: 11, opacity: 0.7 }}>x{p.quantity}</div>
          )}
          {p.vipRequired > 0 && (
            <div style={{ fontSize: 11, color: '#ff9966' }}>VIP {p.vipRequired}+</div>
          )}
        </div>
        <div style={{ fontSize: 13, marginTop: 4, color: meta.color, fontWeight: 600 }}>
          {meta.symbol} {p.price.toLocaleString()}
        </div>
        <button style={{ ...ui.primaryBtn, marginTop: 6 }} onClick={() => buy(p)}>Mua</button>
      </div>
    );
  };

  const active = grouped[tab] ?? [];

  return (
    <div style={{ position: 'absolute', top: 70, left: 10, width: 480, maxHeight: 500, overflow: 'auto', ...ui.panel }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>Cash Shop</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #324a5e', marginBottom: 10 }}>
        {TABS.map((c) => {
          const meta = CURRENCY_META[c];
          const isActive = tab === c;
          const count = grouped[c]?.length ?? 0;
          return (
            <button
              key={c}
              onClick={() => setTab(c)}
              style={{
                background: isActive ? '#0b1220' : 'transparent',
                color: isActive ? meta.color : '#9bb1c2',
                border: '1px solid #324a5e',
                borderBottom: isActive ? '1px solid #0b1220' : '1px solid #324a5e',
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                padding: '6px 12px',
                cursor: 'pointer',
                fontWeight: isActive ? 700 : 500,
                marginBottom: -1,
              }}
            >
              {meta.symbol} {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {list.length === 0 && <div style={{ opacity: 0.6 }}>Chưa có sản phẩm</div>}

      {list.length > 0 && active.length === 0 && (
        <div style={{ opacity: 0.6 }}>Không có sản phẩm trong mục này</div>
      )}

      {active.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
          {active.map(renderCard)}
        </div>
      )}
    </div>
  );
}
